import type {
  CreateUniversalThrottlingPolicyDto,
  UniversalThrottlingMethod,
  UniversalThrottlingMethodName,
  UpdateUniversalThrottlingPolicyDto,
} from "../types/preferences_types";
import {
  useUniversalThrottlingPoliciesQuery,
  useCreatePolicyMutation,
  useUpdatePolicyMutation,
  useDeletePoliciesMutation,
  type CreatePolicyRequest,
  type UpdatePolicyRequest,
} from "../api/universalThrottlingPolicies";

export const UNIVERSAL_THROTTLING_METHODS: UniversalThrottlingMethodName[] = [
  "sleep_sec",
  "max_concurrency",
  "max_run_time_sec",
];

function validateMethods(methods: UniversalThrottlingMethod[]) {
  if (methods.length === 0) return "최소 하나의 스로틀링 메서드가 필요합니다.";

  const keys = new Set(methods.map((m) => m.method));
  const allowed =
    (keys.size === 1 && keys.has("sleep_sec")) ||
    (keys.size === 2 && keys.has("max_concurrency") && keys.has("max_run_time_sec"));

  if (!allowed) return "지원되지 않는 스로틀링 메서드 조합입니다.";

  for (const m of methods) {
    if (!Number.isFinite(m.value)) return `${m.method}는 숫자 값이어야 합니다.`;
    if (m.method === "max_concurrency" && !Number.isInteger(m.value))
      return `${m.method}는 정수 값이어야 합니다.`;
  }
  return null;
}

export function useUniversalThrottlingPolicies() {
  // React Query hooks
  const { data: policies = [], isLoading, refetch } = useUniversalThrottlingPoliciesQuery();
  const createMutation = useCreatePolicyMutation();
  const updateMutation = useUpdatePolicyMutation();
  const deleteMutation = useDeletePoliciesMutation();

  const reload = async () => {
    await refetch();
  };

  const createPolicy = async (dto: CreateUniversalThrottlingPolicyDto) => {
    // 클라이언트 측 유효성 검증
    const v = validateMethods(dto.throttlingMethod);
    if (v) return { ok: false as const, error: v };

    // 중복 체크 (클라이언트 측)
    const dup = policies.some((p) => p.channel === dto.channel && p.throttlingKey === dto.throttlingKey);
    if (dup) return { ok: false as const, error: "동일한 (채널, 스로틀링 키)가 존재합니다." };

    // API 호출
    try {
      const request: CreatePolicyRequest = {
        channel: dto.channel,
        throttlingKey: dto.throttlingKey,
        throttlingMethod: dto.throttlingMethod,
      };
      await createMutation.mutateAsync(request);
      return { ok: true as const };
    } catch (error) {
      // toast는 mutation에서 처리
      return { ok: false as const, error: (error as Error).message };
    }
  };

  const updatePolicy = async (id: string, dto: UpdateUniversalThrottlingPolicyDto) => {
    // 클라이언트 측 유효성 검증
    const v = validateMethods(dto.throttlingMethod);
    if (v) return { ok: false as const, error: v };

    // 중복 체크 (클라이언트 측, 자기 자신 제외)
    const dup = policies.some(
      (p) => p.id !== id && p.channel === dto.channel && p.throttlingKey === dto.throttlingKey
    );
    if (dup) return { ok: false as const, error: "동일한 (채널, 스로틀링 키)가 존재합니다." };

    // API 호출
    try {
      const request: UpdatePolicyRequest = {
        channel: dto.channel,
        throttlingKey: dto.throttlingKey,
        throttlingMethod: dto.throttlingMethod,
      };
      await updateMutation.mutateAsync({ policyId: id, request });
      return { ok: true as const };
    } catch (error) {
      // toast는 mutation에서 처리
      return { ok: false as const, error: (error as Error).message };
    }
  };

  const deletePolicies = async (ids: string[]) => {
    try {
      await deleteMutation.mutateAsync(ids);
      return { ok: true as const };
    } catch (error) {
      // toast는 mutation에서 처리
      return { ok: false as const, error: (error as Error).message };
    }
  };

  return {
    policies,
    reload,
    createPolicy,
    updatePolicy,
    deletePolicies,
    isLoading,
  };
}
